import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableRow,
  Tooltip,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

const DEFAULT_LIMIT = 50
const DEFAULT_CONVERSATION_LIMIT = 50
const DEFAULT_MESSAGES_LIMIT = 200

const getFilenameFromDisposition = (headerValue, fallback) => {
  if (!headerValue) return fallback
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(headerValue)
  if (!match || !match[1]) return fallback
  try {
    return decodeURIComponent(match[1].replace(/"/g, ''))
  } catch (error) {
    return match[1].replace(/"/g, '')
  }
}

const toIsoOrNull = (value) => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toISOString()
}

const formatDateTime = (value) => {
  if (!value) return 'Not checked yet'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString()
}

const formatCount = (value) => {
  const parsed = Number(value || 0)
  if (!Number.isFinite(parsed)) return '0'
  return parsed.toLocaleString()
}

const formatSignupDate = (value) => {
  if (!value) return 'Unknown'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString()
}

const getApiErrorMessage = async (error, fallbackMessage) => {
  const responseData = error?.response?.data
  if (responseData && typeof responseData === 'object' && responseData.error) {
    return responseData.error
  }
  if (responseData instanceof Blob) {
    try {
      const text = await responseData.text()
      const parsed = JSON.parse(text)
      if (parsed?.error) return parsed.error
    } catch (parseError) {}
  }
  return error?.message || fallbackMessage
}

const classifyUser = (user) => {
  const hasFacebook =
    Boolean(user?.fb_user_id) ||
    Boolean(user?.has_fb_auth) ||
    Number(user?.fb_page_count || 0) > 0
  const hasInstagram =
    Boolean(user?.has_ig_login_auth) ||
    (!hasFacebook && Number(user?.ig_account_count || 0) > 0)

  if (hasFacebook) return 'facebook'
  if (hasInstagram) return 'instagram'
  return 'other'
}

const getConversationStateDefault = () => ({
  loading: false,
  error: '',
  page: 1,
  limit: DEFAULT_CONVERSATION_LIMIT,
  total: 0,
  totalPages: 0,
  rows: [],
  platform: '',
  pageId: '',
  igId: '',
  search: '',
  selectedConversationId: null,
  messagesByConversation: {},
})

const tokenColorMap = {
  valid: 'success',
  valid_or_unknown: 'success',
  unknown: 'warning',
  expired: 'warning',
  invalid: 'error',
  missing: 'error',
}

const getTokenChipColor = (status) => tokenColorMap[status] || 'default'
const getTokenTextColor = (status) => {
  const normalized = String(status || '').toLowerCase()
  if (normalized === 'valid' || normalized === 'valid_or_unknown') return 'success.main'
  if (normalized === 'missing' || normalized === 'invalid') return 'error.main'
  if (normalized === 'unknown' || normalized === 'expired') return 'warning.main'
  return 'text.primary'
}

const backButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  borderColor: 'divider',
  backgroundColor: 'grey.100',
}

const formatTokenStatus = (status) => {
  if (!status) return 'Unknown'
  if (status === 'valid') return 'Valid'
  if (status === 'valid_or_unknown') return 'Valid / Unknown'
  if (status === 'unknown') return 'Unknown (not recently verified)'
  if (status === 'expired') return 'Expired'
  if (status === 'invalid') return 'Invalid'
  if (status === 'missing') return 'Missing'
  return String(status)
}

const getTokenExpiryDisplay = (record) =>
  record?.ig_login_token_expires_at ||
  record?.fb_token_expires_at ||
  'Unknown'

const getTokenHelpText = (record, type) => {
  if (record?.token_status === 'missing') {
    if (type === 'fb_page') {
      if (record?.access_error) {
        return `Missing means this page token was not granted or is inaccessible (${record.access_error}).`
      }
      return 'Missing means this page did not grant a page access token.'
    }
    return 'Missing means this asset does not currently have an access token.'
  }
  if (record?.token_status === 'unknown' || record?.token_status === 'valid_or_unknown') {
    return 'Token exists, but has not been recently re-verified via API.'
  }
  return ''
}

const getTokenExpiryLabel = (record) => {
  if (record?.ig_login_token_expires_at) return 'Token expiry'
  if (record?.fb_token_expires_at) return 'User auth expiry'
  return 'Token expiry'
}

const formatConnectionSource = (value) => {
  const source = String(value || '').toLowerCase()
  if (source === 'both') return 'Both (Facebook + Instagram Login)'
  if (source === 'facebook_login') return 'Facebook Login'
  if (source === 'instagram_login') return 'Instagram Login'
  if (!source) return '-'
  return value
}

const truncateText = (value, max = 80) => {
  const text = String(value || '')
  if (text.length <= max) return text
  return `${text.slice(0, Math.max(max - 1, 1))}…`
}

const getOtherParticipantsLabel = (conversation, asset) => {
  const participantIds = Array.isArray(conversation?.participant_ids)
    ? conversation.participant_ids.map((id) => String(id))
    : []
  const participantNames = Array.isArray(conversation?.participant_names)
    ? conversation.participant_names
    : []
  const selfIds = new Set()
  if (asset?.type === 'fb_page' && asset?.page_id) {
    selfIds.add(String(asset.page_id))
  }
  if (asset?.type === 'ig_account' && asset?.ig_id) {
    selfIds.add(String(asset.ig_id))
  }

  const fallbackNames = participantNames
    .map((name) => String(name || '').trim())
    .filter(Boolean)

  if (participantIds.length === 0 || participantNames.length === 0) {
    return fallbackNames.join(', ') || '-'
  }

  const otherNames = []
  for (let i = 0; i < participantIds.length; i += 1) {
    const id = participantIds[i]
    const name = String(participantNames[i] || '').trim()
    if (selfIds.has(id)) continue
    otherNames.push(name || id)
  }
  const uniqueNames = Array.from(new Set(otherNames))
  if (uniqueNames.length > 0) return uniqueNames.join(', ')
  return fallbackNames.join(', ') || '-'
}

const isLikelyInstagramGroupConversation = (conversation) => {
  if (conversation?.platform !== 'instagram') return false
  const participantIds = Array.isArray(conversation?.participant_ids)
    ? conversation.participant_ids.filter(Boolean)
    : []
  const participantNames = Array.isArray(conversation?.participant_names)
    ? conversation.participant_names.filter(Boolean)
    : []

  return participantIds.length > 2 || participantNames.length > 2
}

const getConversationSyncStatus = (conversation) => {
  if (conversation?.message_sync_error_type === 'unsupported_object') {
    if (isLikelyInstagramGroupConversation(conversation)) {
      return {
        label: 'Group chat unavailable',
        color: 'warning',
        detail:
          'Instagram returned this group chat in the conversation list, but the Instagram Messaging API does not support retrieving group-chat messages.',
      }
    }

    return {
      label: 'Unavailable via Instagram API',
      color: 'warning',
      detail:
        conversation?.message_sync_error_message ||
        'Meta returned this conversation in the list, but would not allow this app to retrieve its messages.',
    }
  }

  if (conversation?.messages_complete) {
    return {
      label: 'Finished',
      color: 'success',
      detail: 'Message sync has finished for this conversation.',
    }
  }

  if (conversation?.last_checked || conversation?.message_cursor) {
    return {
      label: 'Partial',
      color: 'warning',
      detail: 'Message sync has started but has not finished yet.',
    }
  }

  return {
    label: 'Queued / not checked yet',
    color: 'default',
    detail:
      'This conversation has been discovered, but message sync has not reached it yet. It should be picked up by a later background sync unless the account or token becomes unavailable.',
  }
}

const sortByName = (rows, key) =>
  [...rows].sort((a, b) =>
    String(a?.[key] || '').localeCompare(String(b?.[key] || ''), undefined, {
      sensitivity: 'base',
    }),
  )

const AdminPortal = () => {
  const defaultBackendUrl =
    typeof window !== 'undefined' ? window.location.origin : ''
  const backendUrl = process.env.REACT_APP_BACKEND_URL || defaultBackendUrl

  const [checkingSession, setCheckingSession] = useState(true)
  const [admin, setAdmin] = useState(null)
  const [authError, setAuthError] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loggingIn, setLoggingIn] = useState(false)
  const [csrfToken, setCsrfToken] = useState('')

  const [search, setSearch] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [page, setPage] = useState(1)
  const [limit] = useState(DEFAULT_LIMIT)
  const [users, setUsers] = useState([])
  const [signupAttempts, setSignupAttempts] = useState([])
  const [attemptsError, setAttemptsError] = useState('')
  const [pagination, setPagination] = useState({
    total: 0,
    total_pages: 0,
  })
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [usersError, setUsersError] = useState('')

  const [selectedUserIds, setSelectedUserIds] = useState([])
  const [connectionsByUser, setConnectionsByUser] = useState({})
  const [conversationsByUser, setConversationsByUser] = useState({})
  const [tokenCheckByKey, setTokenCheckByKey] = useState({})

  const [mode, setMode] = useState('since_last_checked')
  const [updatedFrom, setUpdatedFrom] = useState('')
  const [updatedTo, setUpdatedTo] = useState('')
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')

  const [statusMessage, setStatusMessage] = useState('')
  const [statusError, setStatusError] = useState('')
  const [downloadingKey, setDownloadingKey] = useState(null)
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    user: null,
    submitting: false,
  })

  const [screen, setScreen] = useState({ type: 'list' })

  const authAxios = useMemo(
    () =>
      axios.create({
        baseURL: backendUrl,
        withCredentials: true,
      }),
    [backendUrl],
  )

  const csrfHeaders = useMemo(
    () => (csrfToken ? { 'x-csrf-token': csrfToken } : {}),
    [csrfToken],
  )

  const groupedUsers = useMemo(() => {
    const grouped = {
      facebook: [],
      instagram: [],
      other: [],
    }
    users.forEach((user) => {
      grouped[classifyUser(user)].push(user)
    })
    return grouped
  }, [users])

  const getExportDateParams = useCallback(() => {
    const params = {}
    if (mode !== 'date_range') return params
    const parsedUpdatedFrom = toIsoOrNull(updatedFrom)
    const parsedUpdatedTo = toIsoOrNull(updatedTo)
    const parsedCreatedFrom = toIsoOrNull(createdFrom)
    const parsedCreatedTo = toIsoOrNull(createdTo)
    if (parsedUpdatedFrom) params.updated_from = parsedUpdatedFrom
    if (parsedUpdatedTo) params.updated_to = parsedUpdatedTo
    if (parsedCreatedFrom) params.created_from = parsedCreatedFrom
    if (parsedCreatedTo) params.created_to = parsedCreatedTo
    return params
  }, [mode, updatedFrom, updatedTo, createdFrom, createdTo])

  const loadUsers = useCallback(async () => {
    if (!admin) return
    setLoadingUsers(true)
    setUsersError('')
    setAttemptsError('')
    authAxios.get('/api/admin/signup-attempts')
      .then((res) => setSignupAttempts(res.data?.attempts || []))
      .catch(() => setAttemptsError('Failed to load signup attempts.'))
    try {
      const res = await authAxios.get('/api/admin/users', {
        params: {
          page,
          limit,
          search: search || undefined,
          include_inactive: includeInactive ? 1 : 0,
        },
      })
      setUsers(res.data?.users || [])
      setPagination(
        res.data?.pagination || {
          total: 0,
          total_pages: 0,
        },
      )
    } catch (error) {
      setUsersError(error?.response?.data?.error || 'Failed to load admin users.')
    } finally {
      setLoadingUsers(false)
    }
  }, [admin, authAxios, includeInactive, limit, page, search])

  const loadUserConnections = useCallback(
    async (userId) => {
      setConnectionsByUser((prev) => ({
        ...prev,
        [userId]: {
          ...(prev[userId] || {}),
          loading: true,
          error: '',
        },
      }))

      try {
        const res = await authAxios.get(`/api/admin/users/${userId}/connections`, {
          params: {
            include_inactive: includeInactive ? 1 : 0,
          },
        })
        setConnectionsByUser((prev) => ({
          ...prev,
          [userId]: {
            loading: false,
            error: '',
            data: res.data,
          },
        }))
      } catch (error) {
        setConnectionsByUser((prev) => ({
          ...prev,
          [userId]: {
            ...(prev[userId] || {}),
            loading: false,
            error:
              error?.response?.data?.error || 'Failed to load user connections.',
          },
        }))
      }
    },
    [authAxios, includeInactive],
  )

  const loadUserConversations = useCallback(
    async (userId, stateOverrides = {}) => {
      const current = conversationsByUser[userId] || getConversationStateDefault()
      const nextState = {
        ...current,
        ...stateOverrides,
      }

      setConversationsByUser((prev) => ({
        ...prev,
        [userId]: {
          ...nextState,
          loading: true,
          error: '',
        },
      }))

      try {
        const res = await authAxios.get(`/api/admin/users/${userId}/conversations`, {
          params: {
            page: nextState.page,
            limit: nextState.limit,
            include_inactive: includeInactive ? 1 : 0,
            platform: nextState.platform || undefined,
            page_id: nextState.pageId || undefined,
            ig_id: nextState.igId || undefined,
            search: nextState.search || undefined,
          },
        })

        const rows = res.data?.conversations || []
        const paging = res.data?.pagination || {}

        setConversationsByUser((prev) => {
          const existing = prev[userId] || getConversationStateDefault()
          const selectedId = existing.selectedConversationId
          const selectedExists = rows.some((conv) => conv.id === selectedId)
          return {
            ...prev,
            [userId]: {
              ...nextState,
              loading: false,
              error: '',
              rows,
              total: paging.total || 0,
              totalPages: paging.total_pages || 0,
              selectedConversationId: selectedExists ? selectedId : null,
              messagesByConversation: existing.messagesByConversation || {},
            },
          }
        })
      } catch (error) {
        setConversationsByUser((prev) => ({
          ...prev,
          [userId]: {
            ...nextState,
            loading: false,
            error: error?.response?.data?.error || 'Failed to load conversations.',
          },
        }))
      }
    },
    [authAxios, conversationsByUser, includeInactive],
  )

  const loadConversationMessages = useCallback(
    async (userId, conversationId, pageOverride = 1) => {
      const conversationState =
        conversationsByUser[userId] || getConversationStateDefault()
      const existingMessageState =
        conversationState.messagesByConversation?.[conversationId] || {}

      setConversationsByUser((prev) => {
        const existing = prev[userId] || getConversationStateDefault()
        const existingMessages = existing.messagesByConversation || {}
        return {
          ...prev,
          [userId]: {
            ...existing,
            selectedConversationId: conversationId,
            messagesByConversation: {
              ...existingMessages,
              [conversationId]: {
                ...existingMessages[conversationId],
                loading: true,
                error: '',
                page: pageOverride,
                limit:
                  existingMessages[conversationId]?.limit || DEFAULT_MESSAGES_LIMIT,
              },
            },
          },
        }
      })

      try {
        const res = await authAxios.get(
          `/api/admin/users/${userId}/conversations/${conversationId}/messages`,
          {
            params: {
              page: pageOverride,
              limit: existingMessageState.limit || DEFAULT_MESSAGES_LIMIT,
              include_text: 1,
              include_attachments: 1,
            },
          },
        )

        const rows = res.data?.messages || []
        const paging = res.data?.pagination || {}

        setConversationsByUser((prev) => {
          const existing = prev[userId] || getConversationStateDefault()
          const existingMessages = existing.messagesByConversation || {}
          return {
            ...prev,
            [userId]: {
              ...existing,
              selectedConversationId: conversationId,
              messagesByConversation: {
                ...existingMessages,
                [conversationId]: {
                  loading: false,
                  error: '',
                  rows,
                  page: paging.page || pageOverride,
                  limit: paging.limit || DEFAULT_MESSAGES_LIMIT,
                  total: paging.total || 0,
                  totalPages: paging.total_pages || 0,
                },
              },
            },
          }
        })
      } catch (error) {
        setConversationsByUser((prev) => {
          const existing = prev[userId] || getConversationStateDefault()
          const existingMessages = existing.messagesByConversation || {}
          return {
            ...prev,
            [userId]: {
              ...existing,
              selectedConversationId: conversationId,
              messagesByConversation: {
                ...existingMessages,
                [conversationId]: {
                  ...(existingMessages[conversationId] || {}),
                  loading: false,
                  error: error?.response?.data?.error || 'Failed to load messages.',
                },
              },
            },
          }
        })
      }
    },
    [authAxios, conversationsByUser],
  )

  useEffect(() => {
    const checkSession = async () => {
      setCheckingSession(true)
      try {
        const res = await authAxios.get('/api/admin/me')
        setAdmin(res.data?.admin || null)
        setCsrfToken(res.data?.csrf_token || '')
      } catch (error) {
        setAdmin(null)
        setCsrfToken('')
      } finally {
        setCheckingSession(false)
      }
    }
    checkSession()
  }, [authAxios])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  useEffect(() => {
    const visibleSet = new Set(users.map((user) => user.id))
    setSelectedUserIds((prev) => prev.filter((id) => visibleSet.has(id)))
  }, [users])

  const handleAdminLogin = async () => {
    setAuthError('')
    setLoggingIn(true)
    setStatusError('')
    setStatusMessage('')
    try {
      const res = await authAxios.post('/api/admin/login', {
        username,
        password,
      })
      setAdmin(res.data?.admin || null)
      setCsrfToken(res.data?.csrf_token || '')
      setPassword('')
      setPage(1)
    } catch (error) {
      const status = error?.response?.status || null
      const payload = error?.response?.data || null
      const details = []
      if (status) details.push(`status ${status}`)
      if (payload?.reason) details.push(`reason ${payload.reason}`)
      if (payload?.request_id) details.push(`request ${payload.request_id}`)
      if (payload?.locked_until) details.push(`locked_until ${payload.locked_until}`)
      const base = payload?.error || error?.message || 'Admin login failed.'
      setAuthError(details.length > 0 ? `${base} (${details.join(', ')})` : base)
      setCsrfToken('')
    } finally {
      setLoggingIn(false)
    }
  }

  const handleAdminLogout = async () => {
    try {
      await authAxios.post('/api/admin/logout', {}, { headers: csrfHeaders })
    } catch (error) {}
    setAdmin(null)
    setCsrfToken('')
    setUsers([])
    setSignupAttempts([])
    setSelectedUserIds([])
    setConnectionsByUser({})
    setConversationsByUser({})
    setTokenCheckByKey({})
    setScreen({ type: 'list' })
    setPagination({ total: 0, total_pages: 0 })
  }

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) return prev.filter((id) => id !== userId)
      return [...prev, userId]
    })
  }

  const handleSelectVisibleUsers = () => {
    const visibleUserIds = users.map((user) => user.id)
    const allVisibleSelected =
      visibleUserIds.length > 0 &&
      visibleUserIds.every((id) => selectedUserIds.includes(id))
    if (allVisibleSelected) {
      setSelectedUserIds((prev) =>
        prev.filter((id) => !visibleUserIds.includes(id)),
      )
      return
    }
    setSelectedUserIds((prev) => {
      const merged = new Set(prev)
      visibleUserIds.forEach((id) => merged.add(id))
      return Array.from(merged)
    })
  }

  const downloadUserExport = async (user, format, extraParams = {}) => {
    const key = `${user.id}:${format}`
    setDownloadingKey(key)
    setStatusError('')
    setStatusMessage('')
    try {
      const params = {
        mode,
        format,
        ...extraParams,
        ...getExportDateParams(),
      }
      const response = await authAxios.get(
        `/api/admin/users/${user.id}/export/messages`,
        {
          params,
          responseType: 'blob',
        },
      )
      const contentDisposition = response.headers['content-disposition']
      const fallbackName = `admin-export-user-${user.id}.${format}`
      const filename = getFilenameFromDisposition(contentDisposition, fallbackName)
      const blobUrl = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = blobUrl
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
      setStatusMessage(`Downloaded ${format.toUpperCase()} export for user ${user.id}.`)
    } catch (error) {
      const errMsg = await getApiErrorMessage(
        error,
        `Failed to export ${format.toUpperCase()}.`,
      )
      setStatusError(errMsg)
    } finally {
      setDownloadingKey(null)
    }
  }

  const handleBulkDownload = async (format) => {
    if (selectedUserIds.length === 0) {
      setStatusError('Select at least one user for selected export.')
      return
    }
    const key = `bulk:selected:${format}`
    setDownloadingKey(key)
    setStatusError('')
    setStatusMessage('')
    try {
      const payload = {
        mode,
        format,
        selection_mode: 'selected',
        include_inactive: includeInactive,
        user_ids: selectedUserIds,
        ...getExportDateParams(),
      }
      const response = await authAxios.post(
        '/api/admin/export/messages/bulk',
        payload,
        {
          responseType: 'blob',
          headers: csrfHeaders,
        },
      )
      const contentDisposition = response.headers['content-disposition']
      const fallbackName = `admin-export-bulk-selected.${format}.zip`
      const filename = getFilenameFromDisposition(contentDisposition, fallbackName)
      const blobUrl = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = blobUrl
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
      setStatusMessage(
        `Downloaded ${format.toUpperCase()} bulk export ZIP for ${selectedUserIds.length} selected user(s).`,
      )
    } catch (error) {
      const errMsg = await getApiErrorMessage(
        error,
        `Failed to export bulk ${format.toUpperCase()}.`,
      )
      setStatusError(errMsg)
    } finally {
      setDownloadingKey(null)
    }
  }

  const handleManualTokenCheck = async (userId, payload) => {
    const key = `${userId}:${payload.type}:${payload.page_id || payload.ig_id || 'root'}`
    setTokenCheckByKey((prev) => ({ ...prev, [key]: true }))
    setStatusError('')
    setStatusMessage('')
    try {
      const res = await authAxios.post(
        `/api/admin/users/${userId}/token-check`,
        payload,
        { headers: csrfHeaders },
      )
      setStatusMessage(
        `Token check completed (${payload.type}): ${res.data?.token_status || 'unknown'}`,
      )
      await loadUserConnections(userId)
    } catch (error) {
      const errMsg = await getApiErrorMessage(error, 'Failed to check token status.')
      setStatusError(errMsg)
    } finally {
      setTokenCheckByKey((prev) => ({ ...prev, [key]: false }))
    }
  }

  const openDeleteUserDialog = (user) => {
    setStatusError('')
    setStatusMessage('')
    setDeleteDialog({
      open: true,
      user,
      submitting: false,
    })
  }

  const closeDeleteUserDialog = () => {
    setDeleteDialog((prev) => {
      if (prev.submitting) return prev
      return {
        open: false,
        user: null,
        submitting: false,
      }
    })
  }

  const handleDeleteUser = async () => {
    const user = deleteDialog.user
    if (!user?.id) return
    setDeleteDialog((prev) => ({ ...prev, submitting: true }))
    setStatusError('')
    setStatusMessage('')

    try {
      await authAxios.delete(`/api/admin/users/${user.id}`, {
        headers: csrfHeaders,
      })

      setDeleteDialog({
        open: false,
        user: null,
        submitting: false,
      })
      setStatusMessage(`Deleted user ${user.id} and all associated data.`)
      setSelectedUserIds((prev) => prev.filter((id) => id !== user.id))
      setConnectionsByUser((prev) => {
        const next = { ...prev }
        delete next[user.id]
        return next
      })
      setConversationsByUser((prev) => {
        const next = { ...prev }
        delete next[user.id]
        return next
      })
      setTokenCheckByKey((prev) => {
        const next = {}
        Object.entries(prev).forEach(([key, value]) => {
          if (!key.startsWith(`${user.id}:`)) next[key] = value
        })
        return next
      })
      setScreen((prev) => {
        if (
          (prev.type === 'user' ||
            prev.type === 'conversations' ||
            prev.type === 'conversation') &&
          prev.user?.id === user.id
        ) {
          return { type: 'list' }
        }
        return prev
      })
      await loadUsers()
    } catch (error) {
      const errMsg = await getApiErrorMessage(error, 'Failed to delete user data.')
      setStatusError(errMsg)
      setDeleteDialog((prev) => ({ ...prev, submitting: false }))
    }
  }

  const openUserPage = async (user) => {
    setScreen({ type: 'user', user })
    if (!connectionsByUser[user.id]?.data && !connectionsByUser[user.id]?.loading) {
      await loadUserConnections(user.id)
    }
  }

  const openConversationsPage = async (user, asset) => {
    const current = conversationsByUser[user.id] || getConversationStateDefault()
    const overrides = {
      ...current,
      page: 1,
      pageId: asset.type === 'fb_page' ? asset.page_id : '',
      igId: asset.type === 'ig_account' ? asset.ig_id : '',
      platform: asset.type === 'ig_account' ? 'instagram' : 'facebook',
      search: '',
    }
    setScreen({ type: 'conversations', user, asset })
    await loadUserConversations(user.id, overrides)
  }

  const openConversationDetailPage = async (user, asset, conversation) => {
    setScreen({ type: 'conversation', user, asset, conversation })
    await loadConversationMessages(user.id, conversation.id, 1)
  }

  const renderExportSettings = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Export Settings
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Conversation Updated fields filter by conversation activity time. Message
          Created fields filter by individual message time. Use `All Posts` to ignore
          all time/checkpoint windows.
        </Alert>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="export-mode-label">Export Mode</InputLabel>
              <Select
                labelId="export-mode-label"
                label="Export Mode"
                value={mode}
                onChange={(event) => setMode(event.target.value)}
              >
                <MenuItem value="since_last_checked">Since Last Checked</MenuItem>
                <MenuItem value="date_range">Date Range</MenuItem>
                <MenuItem value="all_posts">All Posts</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeInactive}
                  onChange={(event) => {
                    setIncludeInactive(event.target.checked)
                    setPage(1)
                  }}
                />
              }
              label="Include inactive"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Search users / Page ID / IG ID"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
            />
          </Grid>

          {mode === 'date_range' ? (
            <>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Conversation Updated From"
                  type="datetime-local"
                  value={updatedFrom}
                  onChange={(event) => setUpdatedFrom(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Conversation Updated To"
                  type="datetime-local"
                  value={updatedTo}
                  onChange={(event) => setUpdatedTo(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Message Created From"
                  type="datetime-local"
                  value={createdFrom}
                  onChange={(event) => setCreatedFrom(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Message Created To"
                  type="datetime-local"
                  value={createdTo}
                  onChange={(event) => setCreatedTo(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          ) : null}

          <Grid item xs={12}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="contained"
                onClick={() => handleBulkDownload('csv')}
                disabled={
                  downloadingKey === 'bulk:selected:csv' ||
                  selectedUserIds.length === 0
                }
              >
                {downloadingKey === 'bulk:selected:csv'
                  ? 'Exporting...'
                  : `Export Selected CSV ZIP (${selectedUserIds.length})`}
              </Button>
              <Button
                variant="contained"
                onClick={() => handleBulkDownload('json')}
                disabled={
                  downloadingKey === 'bulk:selected:json' ||
                  selectedUserIds.length === 0
                }
              >
                {downloadingKey === 'bulk:selected:json'
                  ? 'Exporting...'
                  : `Export Selected JSON ZIP (${selectedUserIds.length})`}
              </Button>
              <Button variant="outlined" onClick={loadUsers} disabled={loadingUsers}>
                {loadingUsers ? 'Refreshing...' : 'Refresh Users'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )

  const renderUsersGroupTable = (title, groupUsers, signupLabel) => {
    if (groupUsers.length === 0) return null
    return (
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderWidth: 2,
          borderColor: 'grey.500',
          boxShadow: 'none',
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title} ({groupUsers.length})
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox"></TableCell>
                <TableCell>User</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>FB Pages</TableCell>
                <TableCell>IG Accounts</TableCell>
                <TableCell>Signup</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUserIds.includes(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600 }}>
                      {user.name || '(No name)'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      User ID: {user.id}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Signed up: {formatSignupDate(user.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>{user.active ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{user.fb_page_count || 0}</TableCell>
                  <TableCell>{user.ig_account_count || 0}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={signupLabel}
                      color={signupLabel === 'Facebook' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{ whiteSpace: 'nowrap' }}
                        onClick={() => openUserPage(user)}
                      >
                        Open
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ whiteSpace: 'nowrap' }}
                        onClick={() => downloadUserExport(user, 'csv')}
                        disabled={downloadingKey === `${user.id}:csv`}
                      >
                        {downloadingKey === `${user.id}:csv` ? '...' : 'CSV'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ whiteSpace: 'nowrap' }}
                        onClick={() => downloadUserExport(user, 'json')}
                        disabled={downloadingKey === `${user.id}:json`}
                      >
                        {downloadingKey === `${user.id}:json` ? '...' : 'JSON'}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        sx={{ whiteSpace: 'nowrap' }}
                        onClick={() => openDeleteUserDialog(user)}
                      >
                        Delete User
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    )
  }

  const renderUsersPage = () => {
    const visibleUserIds = users.map((user) => user.id)
    const allVisibleSelected =
      visibleUserIds.length > 0 &&
      visibleUserIds.every((id) => selectedUserIds.includes(id))
    const someVisibleSelected =
      visibleUserIds.some((id) => selectedUserIds.includes(id)) &&
      !allVisibleSelected

    return (
      <>
        <Typography variant="h6" sx={{ mb: 1 }}>Recent signup attempts</Typography>
        {attemptsError && <Alert severity="error">{attemptsError}</Alert>}
        <TableContainer sx={{ mb: 3, maxHeight: 350, overflow: 'auto' }}>
          <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
            <TableHead><TableRow>
              <TableCell>Started</TableCell><TableCell>Route</TableCell>
              <TableCell>Account</TableCell><TableCell>Status</TableCell>
              <TableCell>Last step</TableCell><TableCell>Details</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {signupAttempts.map((attempt) => (
                <TableRow key={attempt.id}>
                  <TableCell>{new Date(attempt.created_at).toLocaleString()}</TableCell>
                  <TableCell>{attempt.provider === 'instagram' ? 'Instagram Direct' : 'Facebook'}</TableCell>
                  <TableCell>{attempt.account_name || 'Not identified'}</TableCell>
                  <TableCell>{attempt.display_status === 'incomplete' ? 'Incomplete' : attempt.status === 'completed' ? 'Connected' : attempt.status}</TableCell>
                  <TableCell>{attempt.checkpoint.replace(/_/g, ' ')}</TableCell>
                  <TableCell sx={{ minWidth: 260, maxWidth: 360, overflowWrap: 'anywhere' }}>
                    {attempt.error_reason || (attempt.display_status === 'incomplete' && ['started', 'redirected'].includes(attempt.checkpoint) ? 'No callback received' : '')}
                    <details><summary>Attempt details</summary>
                      <div>{attempt.id}</div><div>Project: {attempt.project}</div>
                      <div>{attempt.browser}</div>
                      {attempt.events.map((event, index) => <div key={index}>{new Date(event.at).toLocaleTimeString()}: {event.checkpoint.replace(/_/g, ' ')}{event.reason ? ` - ${event.reason}` : ''}</div>)}
                    </details>
                  </TableCell>
                </TableRow>
              ))}
              {!signupAttempts.length && <TableRow><TableCell colSpan={6}>No signup attempts recorded yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Checkbox
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected}
                onChange={handleSelectVisibleUsers}
              />
              <Typography variant="body2">
                Select all users in current page ({selectedUserIds.length} selected)
              </Typography>
            </Stack>
            <Typography variant="body2" color="textSecondary">
              Page {page} of {pagination.total_pages || 1} • Total users:{' '}
              {pagination.total || 0}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page <= 1}
              >
                Prev
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  const totalPages = pagination.total_pages || 1
                  setPage((prev) => Math.min(prev + 1, totalPages))
                }}
                disabled={page >= (pagination.total_pages || 1)}
              >
                Next
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {renderUsersGroupTable(
          'Facebook Signups',
          groupedUsers.facebook,
          'Facebook',
        )}
        {renderUsersGroupTable(
          'Instagram Signups',
          groupedUsers.instagram,
          'Instagram',
        )}
        {renderUsersGroupTable('Other Signups', groupedUsers.other, 'Other')}
      </>
    )
  }

  const renderUserDetailPage = () => {
    const user = screen.user
    const signupType = classifyUser(user)
    const connectionState = connectionsByUser[user.id] || {}
    const data = connectionState.data || {}
    const pages = sortByName(data.pages || [], 'page_name')
    const igAccounts = sortByName(data.ig_accounts || [], 'ig_username')
    const igByPage = igAccounts.reduce((acc, ig) => {
      const key = ig.fb_page_id || '__standalone__'
      if (!acc[key]) acc[key] = []
      acc[key].push(ig)
      return acc
    }, {})
    const standaloneIg = igByPage.__standalone__ || []
    const showFacebookSection = signupType === 'facebook'
    const showInstagramStandaloneSection = signupType === 'instagram'

    return (
      <Stack spacing={2}>
        <Card>
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={1}
            >
              <Box>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setScreen({ type: 'list' })}
                  sx={{ ...backButtonSx, mb: 0.8 }}
                >
                  Back to Users
                </Button>
                <Typography variant="h6">{user.name || '(No name)'}</Typography>
                <Typography variant="body2" color="textSecondary">
                  User ID: {user.id}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Signed up: {formatSignupDate(user.created_at)}
                </Typography>
                <Chip
                  size="small"
                  sx={{ mt: 0.7 }}
                  label={
                    signupType === 'facebook'
                      ? 'Facebook Signup User'
                      : signupType === 'instagram'
                        ? 'Instagram Signup User'
                        : 'Other Signup User'
                  }
                  color={signupType === 'facebook' ? 'primary' : 'secondary'}
                />
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => downloadUserExport(user, 'csv')}
                  disabled={downloadingKey === `${user.id}:csv`}
                >
                  {downloadingKey === `${user.id}:csv` ? 'Exporting...' : 'Export CSV'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => downloadUserExport(user, 'json')}
                  disabled={downloadingKey === `${user.id}:json`}
                >
                  {downloadingKey === `${user.id}:json` ? 'Exporting...' : 'Export JSON'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {connectionState.loading ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : connectionState.error ? (
          <Alert severity="error">{connectionState.error}</Alert>
        ) : (
          <>
            {showFacebookSection ? (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Facebook Pages
                </Typography>
                {pages.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No Facebook pages connected.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {pages.map((pageEntry) => {
                      const linkedIgs = sortByName(
                        igByPage[pageEntry.page_id] || [],
                        'ig_username',
                      )
                      const pageTokenKey = `${user.id}:fb_page:${pageEntry.page_id}`
                      return (
                        <Card key={pageEntry.id} variant="outlined">
                          <CardContent sx={{ pb: '16px !important' }}>
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Box>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {pageEntry.page_name || '(Unnamed Page)'}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Page ID: {pageEntry.page_id || '-'}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Last sync: {formatDateTime(pageEntry.last_sync_at)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Data: {formatCount(pageEntry.conversation_count)} conversations,{' '}
                                  {formatCount(pageEntry.message_count)} messages
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Messages since last export:{' '}
                                  {formatCount(pageEntry.messages_since_last_export)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Token status:{' '}
                                  <Box
                                    component="span"
                                    sx={{ color: getTokenTextColor(pageEntry.token_status) }}
                                  >
                                    {formatTokenStatus(pageEntry.token_status)}
                                  </Box>
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {getTokenExpiryLabel(pageEntry)}:{' '}
                                  {formatDateTime(pageEntry.fb_token_expires_at)}
                                </Typography>
                                {getTokenHelpText(pageEntry, 'fb_page') ? (
                                  <Typography variant="caption" color="textSecondary">
                                    {getTokenHelpText(pageEntry, 'fb_page')}
                                  </Typography>
                                ) : null}
                                <Chip
                                  size="small"
                                  sx={{ mt: 0.7 }}
                                  label={formatTokenStatus(pageEntry.token_status)}
                                  color={getTokenChipColor(pageEntry.token_status)}
                                />
                              </Box>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ whiteSpace: 'nowrap' }}
                                  onClick={() =>
                                    handleManualTokenCheck(user.id, {
                                      type: 'fb_page',
                                      page_id: pageEntry.page_id,
                                    })
                                  }
                                  disabled={Boolean(tokenCheckByKey[pageTokenKey])}
                                >
                                  {tokenCheckByKey[pageTokenKey]
                                    ? 'Checking...'
                                    : 'Check Token'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  sx={{ whiteSpace: 'nowrap' }}
                                  onClick={() =>
                                    openConversationsPage(user, {
                                      type: 'fb_page',
                                      page_id: pageEntry.page_id,
                                      label: pageEntry.page_name || pageEntry.page_id,
                                    })
                                  }
                                >
                                  Conversations
                                </Button>
                              </Stack>
                            </Stack>

                            <Box sx={{ mt: 1 }}>
                              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                Linked Instagram Accounts
                              </Typography>
                              {linkedIgs.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">
                                  No linked IG accounts for this page.
                                </Typography>
                              ) : (
                                <Stack spacing={1}>
                                  {linkedIgs.map((ig) => {
                                    const igTokenKey = `${user.id}:ig_account:${ig.ig_id}`
                                    return (
                                      <Card key={ig.id} variant="outlined">
                                        <CardContent sx={{ pb: '16px !important' }}>
                                          <Stack
                                            direction={{ xs: 'column', md: 'row' }}
                                            justifyContent="space-between"
                                            spacing={1}
                                          >
                                            <Box>
                                              <Typography sx={{ fontWeight: 600 }}>
                                                {ig.ig_username || '(Unknown IG username)'}
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                color="textSecondary"
                                              >
                                                IG ID: {ig.ig_id}
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                color="textSecondary"
                                              >
                                                Source: {formatConnectionSource(ig.connection_source)}
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                color="textSecondary"
                                              >
                                                Last sync: {formatDateTime(ig.last_sync_at)}
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                color="textSecondary"
                                              >
                                                Data: {formatCount(ig.conversation_count)} conversations,{' '}
                                                {formatCount(ig.message_count)} messages
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                color="textSecondary"
                                              >
                                                Messages since last export:{' '}
                                                {formatCount(ig.messages_since_last_export)}
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                color="textSecondary"
                                              >
                                                Token status:{' '}
                                                <Box
                                                  component="span"
                                                  sx={{ color: getTokenTextColor(ig.token_status) }}
                                                >
                                                  {formatTokenStatus(ig.token_status)}
                                                </Box>
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                color="textSecondary"
                                              >
                                                {getTokenExpiryLabel(ig)}:{' '}
                                                {formatDateTime(getTokenExpiryDisplay(ig))}
                                              </Typography>
                                              {getTokenHelpText(ig, 'ig_account') ? (
                                                <Typography
                                                  variant="caption"
                                                  color="textSecondary"
                                                >
                                                  {getTokenHelpText(ig, 'ig_account')}
                                                </Typography>
                                              ) : null}
                                              <Chip
                                                size="small"
                                                sx={{ mt: 0.7 }}
                                                label={formatTokenStatus(ig.token_status)}
                                                color={getTokenChipColor(ig.token_status)}
                                              />
                                            </Box>
                                            <Stack direction="row" spacing={1}>
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                sx={{ whiteSpace: 'nowrap' }}
                                                onClick={() =>
                                                  handleManualTokenCheck(user.id, {
                                                    type: 'ig_account',
                                                    ig_id: ig.ig_id,
                                                  })
                                                }
                                                disabled={Boolean(tokenCheckByKey[igTokenKey])}
                                              >
                                                {tokenCheckByKey[igTokenKey]
                                                  ? 'Checking...'
                                                  : 'Check Token'}
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="contained"
                                                sx={{ whiteSpace: 'nowrap' }}
                                                onClick={() =>
                                                  openConversationsPage(user, {
                                                    type: 'ig_account',
                                                    ig_id: ig.ig_id,
                                                    label: ig.ig_username || ig.ig_id,
                                                  })
                                                }
                                              >
                                                Conversations
                                              </Button>
                                            </Stack>
                                          </Stack>
                                        </CardContent>
                                      </Card>
                                    )
                                  })}
                                </Stack>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      )
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>
            ) : null}

            {showInstagramStandaloneSection ? (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Instagram Accounts
                </Typography>
                {standaloneIg.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No standalone Instagram accounts connected.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {sortByName(standaloneIg, 'ig_username').map((ig) => {
                      const igTokenKey = `${user.id}:ig_account:${ig.ig_id}`
                      return (
                        <Card key={ig.id} variant="outlined">
                          <CardContent sx={{ pb: '16px !important' }}>
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Box>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {ig.ig_username || '(Unknown IG username)'}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  IG ID: {ig.ig_id}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Source: {formatConnectionSource(ig.connection_source)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Last sync: {formatDateTime(ig.last_sync_at)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Data: {formatCount(ig.conversation_count)} conversations,{' '}
                                  {formatCount(ig.message_count)} messages
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Messages since last export:{' '}
                                  {formatCount(ig.messages_since_last_export)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Token status:{' '}
                                  <Box
                                    component="span"
                                    sx={{ color: getTokenTextColor(ig.token_status) }}
                                  >
                                    {formatTokenStatus(ig.token_status)}
                                  </Box>
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {getTokenExpiryLabel(ig)}:{' '}
                                  {formatDateTime(getTokenExpiryDisplay(ig))}
                                </Typography>
                                {getTokenHelpText(ig, 'ig_account') ? (
                                  <Typography variant="caption" color="textSecondary">
                                    {getTokenHelpText(ig, 'ig_account')}
                                  </Typography>
                                ) : null}
                                <Chip
                                  size="small"
                                  sx={{ mt: 0.7 }}
                                  label={formatTokenStatus(ig.token_status)}
                                  color={getTokenChipColor(ig.token_status)}
                                />
                              </Box>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ whiteSpace: 'nowrap' }}
                                  onClick={() =>
                                    handleManualTokenCheck(user.id, {
                                      type: 'ig_account',
                                      ig_id: ig.ig_id,
                                    })
                                  }
                                  disabled={Boolean(tokenCheckByKey[igTokenKey])}
                                >
                                  {tokenCheckByKey[igTokenKey]
                                    ? 'Checking...'
                                    : 'Check Token'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  sx={{ whiteSpace: 'nowrap' }}
                                  onClick={() =>
                                    openConversationsPage(user, {
                                      type: 'ig_account',
                                      ig_id: ig.ig_id,
                                      label: ig.ig_username || ig.ig_id,
                                    })
                                  }
                                >
                                  Conversations
                                </Button>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      )
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>
            ) : null}
          </>
        )}
      </Stack>
    )
  }

  const renderConversationsPage = () => {
    const { user, asset } = screen
    const state = conversationsByUser[user.id] || getConversationStateDefault()
    const assetExportParams =
      asset.type === 'fb_page'
        ? { page_id: asset.page_id, platform: 'facebook' }
        : { ig_id: asset.ig_id, platform: 'instagram' }

    return (
      <Stack spacing={2}>
        <Card>
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={1}
            >
              <Box>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setScreen({ type: 'user', user })}
                  sx={{ ...backButtonSx, mb: 0.8 }}
                >
                  Back to User
                </Button>
                <Typography variant="h6">
                  Conversations for {asset.label || 'Asset'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  User: {user.name || '(No name)'} (ID {user.id})
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => downloadUserExport(user, 'csv', assetExportParams)}
                  disabled={downloadingKey === `${user.id}:csv`}
                >
                  {downloadingKey === `${user.id}:csv` ? 'Exporting...' : 'Export CSV'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => downloadUserExport(user, 'json', assetExportParams)}
                  disabled={downloadingKey === `${user.id}:json`}
                >
                  {downloadingKey === `${user.id}:json` ? 'Exporting...' : 'Export JSON'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search conversations"
                  value={state.search}
                  onChange={(event) =>
                    setConversationsByUser((prev) => ({
                      ...prev,
                      [user.id]: {
                        ...(prev[user.id] || getConversationStateDefault()),
                        search: event.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() =>
                    loadUserConversations(user.id, {
                      ...state,
                      page: 1,
                    })
                  }
                  disabled={state.loading}
                >
                  {state.loading ? 'Loading...' : 'Refresh Conversations'}
                </Button>
              </Grid>
            </Grid>

            {state.error ? (
              <Alert severity="error" sx={{ mt: 1 }}>
                {state.error}
              </Alert>
            ) : null}

            <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 1 }}>
              Total: {state.total || 0}
            </Typography>

            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '15%' }}>Conversation ID</TableCell>
                  <TableCell sx={{ width: '15%' }}>Participant</TableCell>
                  <TableCell sx={{ width: '12%' }}>Platform</TableCell>
                  <TableCell sx={{ width: '14%' }}>Updated</TableCell>
                  <TableCell sx={{ width: '20%' }}>Snippet</TableCell>
                  <TableCell sx={{ width: '12%' }}>Sync</TableCell>
                  <TableCell sx={{ width: '12%' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(state.rows || []).map((conversation) => {
                  const syncStatus = getConversationSyncStatus(conversation)
                  return (
                    <TableRow key={conversation.id}>
                      <TableCell>
                        <Tooltip
                          title={conversation.conversation_id || String(conversation.id)}
                          placement="top-start"
                        >
                          <Typography variant="body2" noWrap>
                            {truncateText(
                              conversation.conversation_id || String(conversation.id),
                              34,
                            )}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={getOtherParticipantsLabel(conversation, asset)}
                          placement="top-start"
                        >
                          <Typography variant="body2" noWrap>
                            {truncateText(getOtherParticipantsLabel(conversation, asset), 34)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{conversation.platform || '-'}</TableCell>
                      <TableCell>{formatDateTime(conversation.updated_time)}</TableCell>
                      <TableCell>
                        <Tooltip title={conversation.snippet || '-'} placement="top-start">
                          <Typography variant="body2" noWrap>
                            {truncateText(conversation.snippet || '-', 40)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={syncStatus.detail} placement="top-start">
                          <Chip
                            size="small"
                            label={syncStatus.label}
                            color={syncStatus.color}
                            variant={syncStatus.color === 'default' ? 'outlined' : 'filled'}
                            sx={{ maxWidth: '100%' }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          sx={{ whiteSpace: 'nowrap' }}
                          onClick={() =>
                            openConversationDetailPage(user, asset, conversation)
                          }
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>

            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  loadUserConversations(user.id, {
                    ...state,
                    page: Math.max(state.page - 1, 1),
                  })
                }
                disabled={state.page <= 1 || state.loading}
              >
                Prev
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  loadUserConversations(user.id, {
                    ...state,
                    page: Math.min(state.page + 1, state.totalPages || 1),
                  })
                }
                disabled={state.page >= (state.totalPages || 1) || state.loading}
              >
                Next
              </Button>
              <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                Page {state.page} of {state.totalPages || 1}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    )
  }

  const renderConversationDetailPage = () => {
    const { user, asset, conversation } = screen
    const state = conversationsByUser[user.id] || getConversationStateDefault()
    const messagesState = state.messagesByConversation?.[conversation.id] || {}
    const rows = messagesState.rows || []
    const syncStatus = getConversationSyncStatus(conversation)

    return (
      <Stack spacing={2}>
        <Card>
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={1}
            >
              <Box>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setScreen({ type: 'conversations', user, asset })}
                  sx={{ ...backButtonSx, mb: 0.8 }}
                >
                  Back to Conversations
                </Button>
                <Typography variant="h6">
                  Conversation {conversation.conversation_id || conversation.id}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Participant: {getOtherParticipantsLabel(conversation, asset)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Sync: {syncStatus.label}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            {syncStatus.color === 'warning' ? (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {syncStatus.detail}
              </Alert>
            ) : null}
            {messagesState.error ? (
              <Alert severity="error" sx={{ mb: 1 }}>
                {messagesState.error}
              </Alert>
            ) : null}
            {messagesState.loading ? (
              <Box sx={{ py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Text</TableCell>
                    <TableCell>Outgoing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((message) => (
                    <TableRow key={message.id}>
                      <TableCell>{formatDateTime(message.created_time)}</TableCell>
                      <TableCell>{message.from_name || message.from_id || '-'}</TableCell>
                      <TableCell>{message.to_name || message.to_id || '-'}</TableCell>
                      <TableCell>{message.text || '-'}</TableCell>
                      <TableCell>{message.is_outgoing ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  loadConversationMessages(
                    user.id,
                    conversation.id,
                    Math.max((messagesState.page || 1) - 1, 1),
                  )
                }
                disabled={
                  messagesState.loading || (messagesState.page || 1) <= 1
                }
              >
                Prev
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  loadConversationMessages(
                    user.id,
                    conversation.id,
                    Math.min(
                      (messagesState.page || 1) + 1,
                      messagesState.totalPages || 1,
                    ),
                  )
                }
                disabled={
                  messagesState.loading ||
                  (messagesState.page || 1) >= (messagesState.totalPages || 1)
                }
              >
                Next
              </Button>
              <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                Page {messagesState.page || 1} of {messagesState.totalPages || 1}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    )
  }

  if (checkingSession) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!admin) {
    return (
      <Grid container justifyContent="center" sx={{ mt: 8 }}>
        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Admin Login
              </Typography>
              <TextField
                fullWidth
                margin="normal"
                label="Username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !loggingIn) handleAdminLogin()
                }}
              />
              {authError ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {authError}
                </Alert>
              ) : null}
              <Button
                sx={{ mt: 2 }}
                fullWidth
                variant="contained"
                onClick={handleAdminLogin}
                disabled={loggingIn || !username || !password}
              >
                {loggingIn ? 'Signing in...' : 'Sign in'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h5">Threat Matrix DMs Admin Console</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2">Signed in as {admin.username}</Typography>
          <Button variant="outlined" onClick={handleAdminLogout}>
            Logout
          </Button>
        </Box>
      </Box>

      {renderExportSettings()}

      {statusMessage ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {statusMessage}
        </Alert>
      ) : null}
      {statusError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {statusError}
        </Alert>
      ) : null}
      {usersError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {usersError}
        </Alert>
      ) : null}

      {screen.type === 'list' && (loadingUsers ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        renderUsersPage()
      ))}
      {screen.type === 'user' && renderUserDetailPage()}
      {screen.type === 'conversations' && renderConversationsPage()}
      {screen.type === 'conversation' && renderConversationDetailPage()}

      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteUserDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete User Data</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete all stored data for{' '}
            <strong>
              {deleteDialog.user?.name || '(No name)'} (ID {deleteDialog.user?.id})
            </strong>
            , including conversations, messages, linked accounts, and auth records.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={closeDeleteUserDialog}
            disabled={deleteDialog.submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
            disabled={deleteDialog.submitting}
          >
            {deleteDialog.submitting ? 'Deleting...' : 'Delete All Data'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AdminPortal
